import { ContentDelete } from '@fs/zion-icon'
import {
  Button,
  DialogOverlay,
  DialogOverlayActions,
  TextField,
  useOverlay,
  DangerButton,
  Paragraph,
} from '@fs/zion-ui'
import PropTypes from 'prop-types'
import React, { useContext, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { deleteAllPatronArtifacts } from '../../../services/artifactService'
import useNotifications from '../../../hooks/useNotifications'
import { PermissionsContext } from '../../app/PermissionsContext'

/**
 * Button shown to Data Admin 6 on the patron artifacts page to
 * delete all of a patron’s artifacts. Manages its own dialog,
 * performs the action, and calls onDelete callback.
 */
const DeleteAllPatronArtifactsButton = ({
  totalArtifactsSpanningPages,
  patronId,
  patronUserName,
  refetch,
  disabled,
}) => {
  const [t] = useTranslation()
  const { isAuthorized } = useContext(PermissionsContext)
  const [idConfirmation, setIdConfirmation] = useState('')
  const confirmDialogOpener = useOverlay()
  const { error } = useNotifications()
  const focusElementRef = useRef()

  const confirmDeleteAll = () => {
    deleteAllPatronArtifacts(patronId)
      .then(() => {
        confirmDialogOpener.close()
        refetch()
      })
      .catch((err) => {
        error(t('artifacts.error.errorDeletingPatronArtifacts', 'Error deleting all patron artifacts'), err)
        confirmDialogOpener.close()
        refetch()
      })
  }

  if (!isAuthorized('ArtPrmCanDeleteOtherUsersData')) {
    return null
  }

  return (
    <>
      <DangerButton Icon={ContentDelete} onClick={confirmDialogOpener.handleClick} disabled={disabled}>
        {`${t('artifacts.deleteAll', 'Delete All')} (${totalArtifactsSpanningPages})`}
      </DangerButton>
      <DialogOverlay
        focusRef={focusElementRef}
        title={t('artifacts.deleteAllArtifacts', 'Delete All Artifacts')}
        {...confirmDialogOpener}
      >
        <Paragraph size="sm">
          {t(
            'artifacts.deleteAllConfirmation',
            'Are you sure you want to permanently delete all artifacts uploaded by { patron }?',
            {
              patron: patronUserName,
            }
          )}
        </Paragraph>
        <Paragraph size="sm">
          {`${t('artifacts.restrictAllEnterPatronId', 'Please type in this patron’s ID to confirm.')} (${patronId})`}
        </Paragraph>
        <TextField
          ref={focusElementRef}
          id="restrictAllPatronIdInput"
          label={t('artifacts.patronId', 'Patron ID')}
          value={idConfirmation}
          onChange={(e) => setIdConfirmation(e.target.value)}
          type="text"
          fullwidth="true"
        />
        <DialogOverlayActions>
          <Button onClick={confirmDeleteAll} emphasis="high" disabled={Number(idConfirmation) !== patronId}>
            {t('action.submit', 'Submit')}
          </Button>
          <Button onClick={confirmDialogOpener.close}>{t('action.cancel', 'Cancel')}</Button>
        </DialogOverlayActions>
      </DialogOverlay>
    </>
  )
}

DeleteAllPatronArtifactsButton.propTypes = {
  totalArtifactsSpanningPages: PropTypes.number.isRequired,
  patronId: PropTypes.number.isRequired,
  patronUserName: PropTypes.string.isRequired,
  refetch: PropTypes.func,
  disabled: PropTypes.bool.isRequired,
}

export default DeleteAllPatronArtifactsButton
